import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { createUserProfile, getUserProfile, clearProfileCache } from '../services/userProfileService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserProfile = useCallback(async (userId) => {
    try {
      const profile = await getUserProfile(userId);
      setUserProfile(profile);
      setError(null);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Failed to load user profile. Please try refreshing the page.');
    }
  }, []);

  useEffect(() => {
    let unsubscribeAuth = null;
    let isMounted = true;

    const setupAuth = () => {
      unsubscribeAuth = onAuthStateChanged(auth, (user) => {
        if (!isMounted) return;
        
        setUser(user);
        if (user) {
          fetchUserProfile(user.uid);
        } else {
          setUserProfile(null);
          setError(null);
        }
        if (isMounted) {
          setLoading(false);
        }
      }, (error) => {
        console.error('Auth state change error:', error);
        if (isMounted) {
          setError('Authentication error. Please try refreshing the page.');
        }
      });
    };

    setupAuth();

    return () => {
      isMounted = false;
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
    };
  }, [fetchUserProfile]);

  const register = async (email, password, eaId) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      await createUserProfile(userCredential.user.uid, {
        email,
        eaId,
      });

      await sendEmailVerification(userCredential.user);
      
      return userCredential.user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      return userCredential.user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      clearProfileCache();
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    try {
      setError(null);
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const isAuthenticated = useCallback(() => {
    return !!user;
  }, [user]);

  const isEmailVerified = useCallback(() => {
    return user?.emailVerified || false;
  }, [user]);

  const getEaId = useCallback(() => {
    return userProfile?.eaId;
  }, [userProfile]);

  const value = {
    user,
    userProfile,
    loading,
    error,
    register,
    login,
    logout,
    resetPassword,
    resendVerificationEmail,
    isAuthenticated,
    isEmailVerified,
    getEaId,
  };

  if (loading) {
    return null; // or a loading spinner
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 